(() => {
  'use strict';

  const mediaCleanupMap = new WeakMap();

  const registerMediaCleanup = (root, cleanupFn) => {
    if (!root || typeof cleanupFn !== 'function') return;
    const cleanups = mediaCleanupMap.get(root) || [];
    cleanups.push(cleanupFn);
    mediaCleanupMap.set(root, cleanups);
  };

  const runMediaCleanup = (root) => {
    const cleanups = mediaCleanupMap.get(root);
    if (!cleanups || !cleanups.length) return;

    cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn('PDP media cleanup error', error);
      }
    });

    mediaCleanupMap.delete(root);
  };

  const forEachMediaRootInScope = (scope, callback) => {
    if (!scope || typeof callback !== 'function') return;

    if (scope.matches && scope.matches('[data-custom-pdp-media]')) {
      callback(scope);
    }

    scope.querySelectorAll('[data-custom-pdp-media]').forEach((root) => callback(root));
  };

  // When the user moves to an image that belongs to another variant, select that variant in the picker.
  // Dispatching change on <variant-selects> runs Dawn's normal variant flow (price, promo, buy button, URL).
  const selectVariantForMedia = (root, mediaId) => {
    const picker = root.closest('product-info')?.querySelector('variant-selects');
    const variantsEl = picker?.querySelector('[data-variants-json]');
    const selectedEl = picker?.querySelector('[data-selected-variant]');
    if (!variantsEl || !selectedEl) return;

    let variants;
    let currentVariant;
    try {
      variants = JSON.parse(variantsEl.textContent);
      currentVariant = JSON.parse(selectedEl.textContent);
    } catch {
      return;
    }

    const candidates = variants.filter((variant) => String(variant.featured_media?.id) === mediaId);
    if (!candidates.length || candidates.some((variant) => variant.id === currentVariant?.id)) return;

    // Prefer the variant that keeps most of the current selection (e.g. same size), then available ones
    const score = (variant) =>
      variant.options.filter((value, index) => value === currentVariant?.options?.[index]).length * 2 +
      (variant.available ? 1 : 0);
    const targetVariant = candidates.reduce((best, variant) => (score(variant) > score(best) ? variant : best));

    const optionGroups = Array.from(picker.querySelectorAll('fieldset, select'));
    targetVariant.options.forEach((value, index) => {
      const group = optionGroups[index];
      if (!group) return;

      if (group.tagName === 'SELECT') {
        const option = Array.from(group.options).find((opt) => opt.value === value);
        if (!option) return;
        group.querySelector('option[selected]')?.removeAttribute('selected');
        option.setAttribute('selected', 'selected');
        group.value = value;
      } else {
        const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) => radio.value === value);
        if (input) input.checked = true;
      }
    });

    // The user is steering the gallery: ignore Dawn's forced "jump to variant image" until they pick an option directly
    root.dataset.mediaDrivenVariant = 'true';
    picker.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // A real option click (trusted event) hands image control back to the variant picker
  document.addEventListener(
    'change',
    (event) => {
      if (!event.isTrusted || !(event.target instanceof Element) || !event.target.closest('variant-selects')) return;
      const root = event.target.closest('product-info')?.querySelector('[data-custom-pdp-media]');
      if (root) delete root.dataset.mediaDrivenVariant;
    },
    true
  );

  const initCustomPdpMedia = (rootNode = document) => {
    rootNode.querySelectorAll('[data-custom-pdp-media]').forEach((root) => {
      if (root.dataset.mediaInitialized === 'true') return;
      root.dataset.mediaInitialized = 'true';

      const mainImages = Array.from(root.querySelectorAll('[data-main-media-id]'));
      const thumbs = Array.from(root.querySelectorAll('[data-target-media-id]'));
      const thumbSwiperEl = root.querySelector('[data-pdp-thumb-swiper]');
      const mainContainer = root.querySelector('.custom-pdp-media__main');
      const valueBadge = root.querySelector('.custom-pdp-media__value-badge');
      const tagsContainer = root.querySelector('.custom-pdp-media__tags');
      const variantFeaturedMediaIds = new Set(
        (root.dataset.variantFeaturedMediaIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      const prevButton = root.querySelector('[data-pdp-media-prev]');
      const nextButton = root.querySelector('[data-pdp-media-next]');
      let thumbSwiper = null;

      if (!mainImages.length) return;

      const getActiveIndex = () => mainImages.findIndex((img) => img.classList.contains('is-active'));

      const setActiveByIndex = (targetIndex) => {
        const total = mainImages.length;
        if (!total) return;

        const normalizedIndex = ((targetIndex % total) + total) % total;
        const activeImage = mainImages[normalizedIndex];
        if (!activeImage) return;

        const mediaId = activeImage.dataset.mainMediaId;
        if (!mediaId) return;

        setActiveMediaByUser(mediaId);
      };

      const syncValueBadge = (mediaId) => {
        if (!tagsContainer) return;

        const normalizedMediaId = String(mediaId);
        const shouldShow = variantFeaturedMediaIds.has(normalizedMediaId);

        // Swap in the tag content (promo discount / value tag) of the variant this image belongs to
        const tagTemplate = root.querySelector(`template[data-media-tags="${normalizedMediaId}"]`);
        if (tagTemplate) {
          tagsContainer.innerHTML = tagTemplate.innerHTML;
          tagsContainer.classList.toggle('on_promo', !!tagsContainer.querySelector('.custom-pdp-media__promo-badge'));
        }

        tagsContainer.hidden = !shouldShow;
        tagsContainer.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };

      const setActiveMedia = (mediaId) => {
        mainImages.forEach((img) => {
          const isActive = img.dataset.mainMediaId === mediaId;
          img.classList.toggle('is-active', isActive);
          img.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        thumbs.forEach((thumb) => {
          const isActive = thumb.dataset.targetMediaId === mediaId;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (thumbSwiper) {
          const activeThumbIndex = thumbs.findIndex((thumb) => thumb.dataset.targetMediaId === mediaId);
          if (activeThumbIndex >= 0) {
            thumbSwiper.slideTo(activeThumbIndex);
          }
        }

        syncValueBadge(mediaId);
      };

      // User-driven switch (arrows, swipe, thumbs): also sync the selected variant
      const setActiveMediaByUser = (mediaId) => {
        setActiveMedia(mediaId);
        selectVariantForMedia(root, mediaId);
      };

      // Public API: allow external code (e.g. variant selection) to switch the active media
      root.setActiveMedia = (mediaId) => {
        if (!mediaId || root.dataset.mediaDrivenVariant === 'true') return;
        setActiveMedia(String(mediaId));
      };

      const handleThumbActivate = (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-target-media-id]') : null;
        if (!target || !root.contains(target)) return;

        const mediaId = target.dataset.targetMediaId;
        if (!mediaId) return;

        event.preventDefault();
        setActiveMediaByUser(mediaId);
      };

      root.addEventListener('click', handleThumbActivate);

      const handlePrevClick = (event) => {
        event.preventDefault();
        const currentIndex = getActiveIndex();
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        setActiveByIndex(safeIndex - 1);
      };

      const handleNextClick = (event) => {
        event.preventDefault();
        const currentIndex = getActiveIndex();
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        setActiveByIndex(safeIndex + 1);
      };

      if (prevButton) {
        prevButton.addEventListener('click', handlePrevClick);
      }

      if (nextButton) {
        nextButton.addEventListener('click', handleNextClick);
      }

      const thumbKeydownHandlers = [];

      thumbs.forEach((thumb) => {
        const handleThumbKeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            const mediaId = thumb.dataset.targetMediaId;
            if (!mediaId) return;
            event.preventDefault();
            setActiveMediaByUser(mediaId);
          }
        };

        thumb.addEventListener('keydown', handleThumbKeydown);
        thumbKeydownHandlers.push({ thumb, handleThumbKeydown });
      });

      let handleTouchStart = null;
      let handleTouchEnd = null;

      if (mainContainer && mainImages.length > 1) {
        let touchStartX = 0;
        let touchStartY = 0;

        handleTouchStart = (event) => {
          const touch = event.changedTouches && event.changedTouches[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        };

        handleTouchEnd = (event) => {
          const touch = event.changedTouches && event.changedTouches[0];
          if (!touch) return;

          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;

          // Ignore vertical gestures and tiny swipes.
          if (Math.abs(deltaX) < 24 || Math.abs(deltaY) > Math.abs(deltaX)) return;

          const currentIndex = getActiveIndex();
          const safeIndex = currentIndex >= 0 ? currentIndex : 0;

          if (deltaX < 0) {
            setActiveByIndex(safeIndex + 1);
          } else {
            setActiveByIndex(safeIndex - 1);
          }
        };

        mainContainer.addEventListener('touchstart', handleTouchStart, { passive: true });

        mainContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
      }

      registerMediaCleanup(root, () => {
        root.removeEventListener('click', handleThumbActivate);
        if (prevButton) {
          prevButton.removeEventListener('click', handlePrevClick);
        }
        if (nextButton) {
          nextButton.removeEventListener('click', handleNextClick);
        }

        thumbKeydownHandlers.forEach(({ thumb, handleThumbKeydown }) => {
          thumb.removeEventListener('keydown', handleThumbKeydown);
        });

        if (mainContainer && handleTouchStart && handleTouchEnd) {
          mainContainer.removeEventListener('touchstart', handleTouchStart);
          mainContainer.removeEventListener('touchend', handleTouchEnd);
        }

        if (thumbSwiper) {
          thumbSwiper.destroy(true, true);
          thumbSwiper = null;
        }

        delete root.setActiveMedia;
      });

      if (thumbSwiperEl && thumbs.length > 1 && typeof Swiper !== 'undefined') {
        thumbSwiper = new Swiper(thumbSwiperEl, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true,
          preventClicks: false,
          preventClicksPropagation: false,
        });
      }

      const currentActiveMedia = mainImages.find((img) => img.classList.contains('is-active'));
      if (currentActiveMedia && currentActiveMedia.dataset.mainMediaId) {
        setActiveMedia(currentActiveMedia.dataset.mainMediaId);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomPdpMedia(document));
  } else {
    initCustomPdpMedia(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) {
      initCustomPdpMedia(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;

    forEachMediaRootInScope(event.target, (root) => {
      runMediaCleanup(root);
      root.dataset.mediaInitialized = 'false';
    });
  });
})();
